import {
    FcpTreeItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import cloneDeep from 'lodash/cloneDeep';
import { DueDates } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import {
    AccountMappingEngineHelper,
    isSubTotal,
    isSubfli,
} from '../../../../../../../../aumico-lib-templates-fe/src/lib';
import { ClosingEntry, FcpModel } from '../models/fcp.model';

export class ClosingEntryHelper {
    static applyAdjustmentsOnInputFinStatements(domainModel: FcpModel, dueDates: DueDates): FcpModel {
        const closingEntries: ClosingEntry[] = [];
        const newDomainModel = cloneDeep(domainModel);
        function collectClosingEntries(items: FcpTreeItem[]) {
            items.forEach((item) => {
                const currentYearEntriesInItem = item.closingEntries.filter(
                    (closingEntry: ClosingEntry) =>
                        new Date(closingEntry?.dueDate).getTime() === new Date(dueDates?.dueDate).getTime() &&
                        closingEntry.booked === false,
                );
                closingEntries.push(...currentYearEntriesInItem);
                if (item.children && item.children.length > 0) {
                    collectClosingEntries(item.children);
                }
            });
        }

        // Helper function to apply closing entries to accounts
        function applyClosingEntries(
            accounts: IAccount[],
            closingEntries: ClosingEntry[],
            type: FinancialLineItemType,
        ) {
            accounts.forEach((account) => {
                //  check if the accounts is an Account or a SUBFLI
                if (isSubfli(account)) {
                    const subfli = account as IAccountMappingSubFLI;
                    applyClosingEntries(subfli.accounts, closingEntries, type);
                    subfli.calculatedClosingBalance = subfli.accounts.reduce(
                        (sum, acc) => sum + (acc.calculatedClosingBalance || 0),
                        0,
                    );
                } else {
                    closingEntries.forEach((entry) => {
                        if (account.accountNumber === entry.debit) {
                            // Debit account logic
                            switch (type) {
                                case FinancialLineItemType.ASSETS:
                                    account.closingBalance += entry.amount; // Plus for assets
                                    account.calculatedClosingBalance += entry.amount; // Plus for assets
                                    break;
                                case FinancialLineItemType.LIABILITIES:
                                    account.closingBalance -= entry.amount; // Minus for liabilities
                                    account.calculatedClosingBalance -= entry.amount; // Minus for liabilities
                                    break;
                                case FinancialLineItemType.INCOME:
                                    account.closingBalance -= entry.amount; // Minus for income statement
                                    account.calculatedClosingBalance -= entry.amount; // Minus for income statement
                                    break;
                                case FinancialLineItemType.EXPENSES:
                                    account.closingBalance += entry.amount; // Plus for expenses (if relevant)
                                    account.calculatedClosingBalance += entry.amount; // Plus for expenses (if relevant)
                                    break;
                                // Add other cases if necessary
                            }
                        }
                        if (account.accountNumber === entry.credit) {
                            // Credit account logic
                            switch (type) {
                                case FinancialLineItemType.ASSETS:
                                    account.closingBalance -= entry.amount; // Minus for assets
                                    account.calculatedClosingBalance -= entry.amount; // Minus for assets
                                    break;
                                case FinancialLineItemType.LIABILITIES:
                                    account.closingBalance += entry.amount; // Plus for liabilities
                                    account.calculatedClosingBalance += entry.amount; // Plus for liabilities
                                    break;
                                case FinancialLineItemType.INCOME:
                                    account.closingBalance += entry.amount; // Plus for income statement
                                    account.calculatedClosingBalance += entry.amount; // Plus for income statement
                                    break;
                                case FinancialLineItemType.EXPENSES:
                                    account.closingBalance -= entry.amount; // Minus for expenses (if relevant)
                                    account.calculatedClosingBalance -= entry.amount; // Minus for expenses (if relevant)
                                    break;
                                // Add other cases if necessary
                            }
                        }
                    });
                }
            });
        }

        // Recursively apply adjustments to the template and its children
        function applyAdjustmentsToTemplate(template: IAccountMappingTemplate | null, closingEntries: ClosingEntry[]) {
            if (!template) return;

            // Apply to groups
            if (template.group) {
                applyAdjustmentsToTemplateGroup(template.group, closingEntries);
            }

            // Apply to items within the template group
            function applyAdjustmentsToTemplateGroup(
                group: IAccountMappingTemplateGroup,
                closingEntries: ClosingEntry[],
            ) {
                group.sortedItems.forEach((item) => {
                    if (item.type === ItemType.FLI) {
                        const fliItem = item as IAccountMappingTemplateGroupItemFLI;
                        applyClosingEntries(fliItem.accounts, closingEntries, fliItem.fliType);
                        fliItem.calculatedClosingBalance = fliItem.accounts.reduce(
                            (sum, acc) => sum + (acc.calculatedClosingBalance || 0),
                            0,
                        );
                    }
                    if (item.type === ItemType.SUB_TOTAL) {
                        const fliItem = item as IAccountMappingTemplateGroupItemFLI;

                        fliItem.calculatedClosingBalance = 0;
                    }
                    if (item.hasOwnProperty('groups')) {
                        const group = item as unknown as IAccountMappingTemplateGroup;
                        applyAdjustmentsToTemplateGroup(group, closingEntries);
                        group.calculatedClosingBalance = group.items.reduce(
                            (sum, acc) => sum + (acc.calculatedClosingBalance || 0),
                            0,
                        );
                    }
                });

                group.calculatedClosingBalance = group.sortedItems.reduce(
                    (sum, acc) => sum + (acc['calculatedClosingBalance'] || 0),
                    null,
                );
            }
        }

        // Helper function to synchronize updated accounts from the template back to the main account list
        function synchronizeAccounts(accounts: IAccount[], template: IAccountMappingTemplate | null) {
            if (!template) return;

            // Helper function to flatten accounts in template
            function flattenAccountsFromTemplate(group: IAccountMappingTemplateGroup, flatAccounts: IAccount[]) {
                group.sortedItems.forEach((item) => {
                    if (item.type === ItemType.FLI) {
                        const fliItem = item as IAccountMappingTemplateGroupItemFLI;
                        flatAccounts.push(...fliItem.accounts);
                    } else if (item.hasOwnProperty('groups')) {
                        flattenAccountsFromTemplate(item as unknown as IAccountMappingTemplateGroup, flatAccounts);
                    }
                });
            }

            const flatAccounts: IAccount[] = [];
            if (template.group) {
                flattenAccountsFromTemplate(template.group, flatAccounts);
            }

            // Update the main accounts list with the flattened accounts
            accounts.forEach((account) => {
                const updatedAccount = flatAccounts.find((acc) => acc.accountNumber === account.accountNumber);
                if (updatedAccount) {
                    account.closingBalance = updatedAccount.closingBalance;
                    account.calculatedClosingBalance = updatedAccount.calculatedClosingBalance;
                }
            });
        }

        function synchronizeSubTotals(group: IAccountMappingTemplateGroup): void {
            // Create a map of SUB_TOTAL items in sortedItems for quick lookup
            const subTotalMap: Map<string, IAccountMappingTemplateGroupItemSubtotal> = new Map();

            // Populate the map with SUB_TOTAL items from sortedItems
            group.sortedItems?.forEach((sortedItem) => {
                if (isSubTotal(sortedItem)) {
                    const subTotal = sortedItem as IAccountMappingTemplateGroupItemSubtotal;
                    subTotalMap.set(subTotal.name, subTotal);
                }
            });

            // Update corresponding items in the items array
            group.items.forEach((item) => {
                if (isSubTotal(item)) {
                    const itemSubTotal = item as IAccountMappingTemplateGroupItemSubtotal;
                    const correspondingSubTotal = subTotalMap.get(itemSubTotal.name);

                    if (correspondingSubTotal) {
                        itemSubTotal.calculatedClosingBalance = correspondingSubTotal.calculatedClosingBalance;
                        itemSubTotal.calculatedOpeningBalance = correspondingSubTotal.calculatedOpeningBalance;
                    }
                }
            });

            // Recursively synchronize sub-groups
            group.groups.forEach((subGroup) => {
                synchronizeSubTotals(subGroup);
            });
        }

        collectClosingEntries(domainModel.items);
        // Apply the collected closing entries to outputFinStatements
        newDomainModel.outputFinStatements = cloneDeep(newDomainModel.inputFinStatements);
        newDomainModel.outputFinStatements.forEach((finStatement) => {
            if (finStatement.templateAndAccountsMapping.template) {
                applyAdjustmentsToTemplate(finStatement.templateAndAccountsMapping.template, closingEntries);
            }
        });

        newDomainModel.outputFinStatements.forEach((finStatement) => {
            if (finStatement.templateAndAccountsMapping.template) {
                synchronizeAccounts(
                    finStatement.templateAndAccountsMapping.accounts,
                    finStatement.templateAndAccountsMapping.template,
                );
                if (finStatement.templateAndAccountsMapping.balanceSwitchAccounts) {
                    synchronizeAccounts(
                        finStatement.templateAndAccountsMapping.balanceSwitchAccounts,
                        finStatement.templateAndAccountsMapping.template,
                    );
                }
            }
        });

        AccountMappingEngineHelper.calculateGroupWithoutFlisForFCP(
            newDomainModel.outputFinStatements[0].templateAndAccountsMapping.template.group,
            new Map(),
            false,
        );
        AccountMappingEngineHelper.calculateGroupWithoutFlisForFCP(
            newDomainModel.outputFinStatements[1].templateAndAccountsMapping.template.group,
            new Map(),
            false,
        );

        synchronizeSubTotals(newDomainModel.outputFinStatements[1].templateAndAccountsMapping.template.group);

        return newDomainModel;
    }
}
